import React, { FC, useMemo } from 'react';

// components
import AampeTooltip from '../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';

// hooks
import useCMSPlaceholderQuery from '../../../hooks/cms-placeholders/useCMSPlaceholderQuery';

// models
import { CMSPlaceholderModel } from '../../../models/cms-placeholder.model';
import {
    CMSPlaceholderElement,
    ElementKind,
} from '../../../models/editor.model';

interface CMSPlaceholderViewElementProps {
    element: CMSPlaceholderElement;
    handleSelection?: (e: Record<string, any> | null) => void;
    className?: string;
}

const CMSPlaceholderViewElement: FC<
    React.PropsWithChildren<CMSPlaceholderViewElementProps>
> = ({ element, handleSelection, className }) => {
    const { data } = useCMSPlaceholderQuery();
    const details = {
        kind: ElementKind.cms_placeholder,
        elementId: element.id,
        cmsPlaceholderId: element.cmsPlaceholderId,
    };

    const currentPlaceholderItem = useMemo<
        CMSPlaceholderModel | undefined
    >(() => {
        return (data?.messageable ?? []).find(
            (d) => d.id === element.cmsPlaceholderId,
        );
    }, [data, element]);

    const text = useMemo(
        () => currentPlaceholderItem?.name ?? 'Cart reference',
        [currentPlaceholderItem],
    );

    const sampleValue = useMemo<string>(() => {
        let value = '';
        if (element.cmsPlaceholderId) {
            if (currentPlaceholderItem && currentPlaceholderItem.sample_value) {
                value = currentPlaceholderItem.sample_value;
            }
        }
        return value || text;
    }, [currentPlaceholderItem, element, text]);

    return (
        <AampeTooltip title={text}>
            <Text
                className={className}
                onClick={() => handleSelection?.(details)}
            >
                <StyledIcon />
                {sampleValue}
            </Text>
        </AampeTooltip>
    );
};

const Text = styled.span({
    userSelect: 'none',
    outline: 'none',
    display: 'inline-block',
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.interactiveWG01,
    padding: aampeTheme.spacing4(0, 1),
    color: aampeTheme.colors.neutral09,
    lineHeight: '24px',
    fontSize: aampeTheme.size.font.default,
    fontWeight: 400,
    minWidth: '30px',
    border: `1px solid ${aampeTheme.colors.solidA02}`,
});

const StyledIcon = styled(ICONS.cart)({
    lineHeight: '20px',
    verticalAlign: 'middle',
    margin: aampeTheme.spacing4(0, 2, 0, 0),
    color: aampeTheme.colors.textAccessibleWG01,
    width: '16px',
    height: '16px',
});

export default CMSPlaceholderViewElement;
